import React from 'react';

import * as styles from './styles';
import { Divider } from '@cobliteam/ui/components';

import type { UUID, Vehicle } from '@cobliteam/common-types';

import { isWebDataError, isWebDataFetching, isWebDataSuccess, type WebData } from '@cobliteam/api-lib';

import { Analytics } from '@cobliteam/analytics';

import {
  BodyText,
  ButtonGroup,
  DescriptionTitle,
  DialogWithHeader,
  type ITableColumn,
  Loading,
  ModalType,
  NoConnectionIcon,
  Placeholder,
  PlaceholderVehicleIcon,
  Table,
} from '@cobliteam/base-components';

export interface DeleteRemindersModalProps {
  vehiclesWithRemindersWebData: WebData<Vehicle[]>;
  onClose: () => void;
  onConfirm: (vehicleIds: UUID[]) => void;
}

export const DeleteRemindersModal: React.FC<DeleteRemindersModalProps> = ({ vehiclesWithRemindersWebData, onClose, onConfirm }) => {
  const [wantedVehicles, setWantedVehicles] = React.useState<Vehicle[]>([]);

  const handleConfirm = () => {
    Analytics.track('Maintenance Reminders Deletion Confirmed');
    onConfirm(wantedVehicles.map((v) => v.id));
  };

  const handleSelectAll = (select: boolean, vehiclesWithReminders: Vehicle[]) => {
    setWantedVehicles(select ? vehiclesWithReminders : []);
  };

  const handleRowSelection = (selectedRow: Vehicle, selected: boolean) => {
    if (selected) {
      setWantedVehicles(wantedVehicles.concat(selectedRow));
    } else {
      setWantedVehicles(wantedVehicles.filter((row) => row.id !== selectedRow.id));
    }
  };

  const columns: Array<ITableColumn<Vehicle>> = [
    {
      id: 'vehicleName',
      label: 'Selecionar todos',
      sortable: false,
      Cell: (row) => row.label,
    },
  ];

  return (
    <DialogWithHeader
      open
      maxWidth='md'
      title='Descadastrar lembretes de manutenção'
      actions={
        <ButtonGroup
          onCancel={onClose}
          onConfirm={handleConfirm}
          buttonText='Descadastrar'
          confirmationButtonProps={{
            disabled: wantedVehicles.length === 0,
          }}
          type={ModalType.DELETE}
        />
      }
      onCloseButtonClick={onClose}
      scroll='body'
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <BodyText>Você tem certeza que deseja descadastrar os lembretes de manutenção? Você poderá configurá-los novamente e voltar a usá-los a qualquer momento.</BodyText>
          <DescriptionTitle>Selecione os veículos:</DescriptionTitle>
        </div>
        <Divider />
        {isWebDataSuccess(vehiclesWithRemindersWebData) && vehiclesWithRemindersWebData.data.length > 0 && (
          <Table
            orderBy={columns[0]}
            order='asc'
            selectionConfig={{
              selectable: true,
              className: styles.checkboxColumn,
            }}
            columns={columns}
            rows={vehiclesWithRemindersWebData.data}
            selected={wantedVehicles}
            className={styles.table}
            onRowSelection={handleRowSelection}
            onSelectAllClick={(select) => handleSelectAll(select, vehiclesWithRemindersWebData.data)}
            tableTitle='Selecione os veículos'
          />
        )}
        {isWebDataSuccess(vehiclesWithRemindersWebData) && vehiclesWithRemindersWebData.data.length === 0 && (
          <Placeholder className={styles.placeholderWithoutTextButton} text='Não há veículos com lembretes de manutenção configurados.' Icon={PlaceholderVehicleIcon} />
        )}
        {isWebDataError(vehiclesWithRemindersWebData) && (
          <Placeholder className={styles.placeholderWithoutTextButton} text='Ops! Ocorreu um erro no sistema.' Icon={NoConnectionIcon} />
        )}
        {isWebDataFetching(vehiclesWithRemindersWebData) && <Loading className={styles.loading} />}
        <Divider />
      </div>
    </DialogWithHeader>
  );
};
